import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';

import Form from 'components/shared/Form';

import { ItemContext } from 'components/shared/context/ItemContextProvider';
import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import BaseInput from 'components/shared/inputs/BaseInput';
import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import BaseModal from 'components/shared/modals/BaseModal';

import Type from 'schema/type';

import sansanTech from 'services/sansantech';

interface EditTypeModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  type: Type | null;
  resetType: () => void;
}

const EditTypeModal: React.FC<EditTypeModalProps> = ({
  isOpen,
  setIsOpen,
  type,
  resetType,
}) => {
  const { userState } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);
  const { item, setItem } = useContext(ItemContext);

  const [submitting, setSubmitting] = useState(false);

  const [typeNameValue, setTypeNameValue] = useState<string>('');
  const [isTypeNameValueValid, setIsTypeNameValueValid] =
    useState<boolean>(false);

  const [typePriceValue, setTypePriceValue] = useState<string>('');
  const [isTypePriceValueValid, setIsTypePriceValueValid] =
    useState<boolean>(false);

  const [typeCostValue, setTypeCostValue] = useState<string>('');
  const [isTypeCostValueValid, setIsTypeCostValueValid] =
    useState<boolean>(true);

  const [typeMeasurementUnitValue, setTypeMeasurementUnitValue] =
    useState<string>('');

  useEffect(() => {
    if (type) {
      setTypeNameValue(type.name);
      setTypePriceValue(String(type.price));
      setTypeCostValue(type.cost?.toString() || '');
      setTypeMeasurementUnitValue(type.measurementUnit || '');
      setIsTypeNameValueValid(true);
      setIsTypePriceValueValid(true);
    }
  }, [type]);

  const reset = useCallback(() => {
    setTypeNameValue('');
    setIsTypeNameValueValid(false);
    setTypePriceValue('');
    setIsTypePriceValueValid(false);
    setTypeCostValue('');
    setIsTypeCostValueValid(true);
    setTypeMeasurementUnitValue('');
    resetType();
  }, [resetType]);

  const onSubmit = useCallback(() => {
    setSubmitting(true);
    if (userState.token && item && type) {
      sansanTech
        .updateType(userState.token, type.id, {
          name: typeNameValue,
          price: parseFloat(typePriceValue),
          cost: typeCostValue.length ? parseFloat(typeCostValue) : null,
          measurementUnit: typeMeasurementUnitValue.length
            ? typeMeasurementUnitValue
            : null,
        })
        .then((response) => {
          console.log(response);
          setItem?.((currentItem) => {
            const updatedType = response.data;
            if (currentItem && currentItem.categories) {
              const category = currentItem.categories.find(
                (currentItemCategory) => {
                  if (currentItemCategory.types) {
                    const typeIds = currentItemCategory.types.map(
                      (type) => type.id
                    );
                    if (typeIds.includes(updatedType.id)) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }
              );
              if (category && category.types) {
                const typeIndex = category.types.findIndex(
                  (categoryType) => categoryType.id === updatedType.id
                );
                category.types[typeIndex] = updatedType;
              }
            }
            return currentItem;
          });
          setSubmitting(false);
          toastDispatch?.({
            type: 'SUCCESS',
            message: 'Edited type',
          });
          setIsOpen(false);
          reset();
        })
        .catch((error) => {
          console.log(error);
          setSubmitting(false);
          toastDispatch?.({
            type: 'ERROR',
            message: 'Could not edit type',
          });
        });
    } else {
      setSubmitting(false);
      toastDispatch?.({ type: 'ERROR', message: 'No permission to do this' });
    }
  }, [
    reset,
    toastDispatch,
    userState.token,
    setIsOpen,
    item,
    typeNameValue,
    typePriceValue,
    typeCostValue,
    typeMeasurementUnitValue,
    setItem,
    type,
  ]);

  const disabled = useMemo(
    () =>
      !(isTypeNameValueValid && isTypePriceValueValid && isTypeCostValueValid),
    [isTypeCostValueValid, isTypeNameValueValid, isTypePriceValueValid]
  );

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Edit type"
      onClose={reset}
    >
      <Form
        header="Edit type"
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={disabled}
        submitButtonText="Edit type"
        submitButtonWidth="110px"
      >
        <ValidatedBaseInput
          label="type name"
          type="text"
          value={typeNameValue}
          setValue={setTypeNameValue}
          setIsValid={setIsTypeNameValueValid}
          validators={z.string().min(1, { message: 'This field is required' })}
        />
        <ValidatedBaseInput
          label="type price"
          type="number"
          value={typePriceValue}
          setValue={setTypePriceValue}
          setIsValid={setIsTypePriceValueValid}
          validators={z
            .number({
              required_error: 'This field is required',
              invalid_type_error: 'This field is required',
            })
            .positive({ message: 'Must be greater than 0' })}
        />
        <ValidatedBaseInput
          label="type cost"
          type="number"
          value={typeCostValue}
          setValue={setTypeCostValue}
          setIsValid={setIsTypeCostValueValid}
          validators={z
            .number()
            .positive({ message: 'Must be greater than 0' })}
          allowEmptyValue={true}
        />
        <BaseInput
          label="type measurement unit"
          type="text"
          value={typeMeasurementUnitValue}
          setValue={setTypeMeasurementUnitValue}
        />
      </Form>
    </BaseModal>
  );
};

export default EditTypeModal;
